import React, { useEffect, useState } from 'react';
import { Loader, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useCustomerPermissions } from '../../hooks/useCustomerPermissions';

/**
 * Quick-add a PROSPECT (CRM-FE-1). Used by the customers list, and by the
 * meeting form (CRM-FE-2), so a salesperson standing in a lease trailer can
 * log a first meeting with a company the office has never heard of.
 *
 * ── TWO REQUESTS, IN THIS ORDER ────────────────────────────────────────────
 * 1. POST /customers with `status: 'prospect'` and the main-contact fields
 *    (camelCase — the customers route reads Drizzle property names).
 * 2. POST /customers/:id/contacts with the contact, when this user may write
 *    contacts and named one. A SALES user may not (CUSTOMER_WRITE_ROLES gate,
 *    api#1038), so for sales the contact rides on the customer's own
 *    main-contact fields only, and the rig field — which exists only on a
 *    contact row — is not offered.
 *
 * If step 2 fails the prospect still exists. That is said, not hidden, and the
 * caller still receives the new customer: refusing to hand back a row the
 * server created would make the reader create it twice.
 *
 * ── A 409 IS AN ANSWER ─────────────────────────────────────────────────────
 * POST /customers answers 409 `{ error, existingId }` when the name matches a
 * live customer (case and trailing punctuation ignored). That is the API
 * saying "you already have this one", so it is offered as "Use the existing
 * customer", never as an error to retry.
 */
export interface CreatedProspect {
  id: string;
  name: string;
  status?: string;
  [key: string]: unknown;
}

export interface QuickAddProspectProps {
  open: boolean;
  initialName?: string;
  onClose: () => void;
  onCreated: (customer: CreatedProspect) => void;
  /** The 409's `existingId`. */
  onUseExisting: (customerId: string) => void;
}

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:border-fi-amber';

function apiMessage(err: any, fallback: string): string {
  const e = err?.response?.data?.error;
  if (typeof e === 'string' && e.trim()) return e;
  // customer-contacts answers zod's fieldErrors as an OBJECT.
  if (e && typeof e === 'object') {
    const first = Object.entries(e).find(([, v]) => Array.isArray(v) && v.length);
    if (first) return `${first[0]}: ${(first[1] as string[])[0]}`;
  }
  return fallback;
}

export function QuickAddProspect({ open, initialName = '', onClose, onCreated, onUseExisting }: QuickAddProspectProps) {
  const queryClient = useQueryClient();
  const { canWriteContacts } = useCustomerPermissions();
  const [name, setName] = useState(initialName);
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [rig, setRig] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ id: string; message: string } | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setContactName('');
      setPhone('');
      setEmail('');
      setRig('');
      setError(null);
      setDuplicate(null);
      setPending(false);
    }
  }, [open, initialName]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || pending) return;
    setPending(true);
    setError(null);
    setDuplicate(null);

    let created: CreatedProspect;
    try {
      const res = await api.customers.create({
        name: name.trim(),
        status: 'prospect',
        mainContactName: contactName.trim() || null,
        mainContactPhone: phone.trim() || null,
        mainContactEmail: email.trim() || null,
      });
      const body = res.data;
      created = ('data' in body ? body.data : body) as CreatedProspect;
    } catch (err: any) {
      setPending(false);
      const existingId = err?.response?.status === 409 ? err.response.data?.existingId : null;
      if (existingId) {
        // The API's sentence carries the existing row's uuid, which means
        // nothing to the reader; keep the name it quotes and drop the id.
        const quoted = /matches existing '(.+)'/.exec(apiMessage(err, ''))?.[1];
        setDuplicate({
          id: existingId,
          message: quoted ? `${quoted} is already a customer.` : 'A customer with this name already exists.',
        });
        return;
      }
      setError(apiMessage(err, 'Could not add the prospect.'));
      return;
    }

    if (canWriteContacts && contactName.trim()) {
      try {
        await api.customers.createContact(created.id, {
          name: contactName.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
          rigName: rig.trim() || null,
        });
        queryClient.invalidateQueries({ queryKey: ['customer-contacts', created.id] });
      } catch (err: any) {
        toast.error(`Prospect added, but the contact was not saved: ${apiMessage(err, 'unknown error')}`);
      }
    }

    queryClient.invalidateQueries({ queryKey: ['customers'] });
    setPending(false);
    onCreated(created);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget && !pending) onClose(); }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-add-prospect-title"
        onSubmit={submit}
        className="flex max-h-[92vh] w-full max-w-md flex-col rounded-t-2xl bg-fieldiq-card shadow-2xl sm:mx-4 sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-fieldiq-border px-5 py-4">
          <h3 id="quick-add-prospect-title" className="text-lg font-bold text-white">New prospect</h3>
          <button type="button" onClick={onClose} disabled={pending} aria-label="Close" className="text-fieldiq-muted hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-auto px-5 py-4">
          {duplicate && (
            <div role="alert" className="rounded-lg border border-fieldiq-border bg-fieldiq-dark p-3 text-sm text-fieldiq-text">
              <p>{duplicate.message}</p>
              <button
                type="button"
                onClick={() => onUseExisting(duplicate.id)}
                className="fi-action fi-action--standard fi-action--primary mt-2"
              >
                Use the existing customer
              </button>
            </div>
          )}
          {error && (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-fieldiq-text">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="qap-name" className="mb-1 block text-sm text-white">Company name *</label>
            <input id="qap-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} autoFocus autoComplete="off" />
          </div>
          <div>
            <label htmlFor="qap-contact" className="mb-1 block text-sm text-white">Contact name</label>
            <input id="qap-contact" value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputClass} autoComplete="off" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="qap-phone" className="mb-1 block text-sm text-white">Phone</label>
              <input id="qap-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} autoComplete="off" />
            </div>
            <div>
              <label htmlFor="qap-email" className="mb-1 block text-sm text-white">Email</label>
              <input id="qap-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="off" />
            </div>
          </div>
          {canWriteContacts && (
            <div>
              <label htmlFor="qap-rig" className="mb-1 block text-sm text-white">Rig</label>
              <input id="qap-rig" value={rig} onChange={(e) => setRig(e.target.value)} className={inputClass} autoComplete="off" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-fieldiq-border px-5 py-4">
          <button type="button" onClick={onClose} disabled={pending} className="px-4 py-2 text-sm text-white">
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending || !name.trim()}
            className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
          >
            {pending && <Loader size={14} className="animate-spin" aria-hidden="true" />}
            {pending ? 'Adding…' : 'Add prospect'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default QuickAddProspect;
